/* Native Shopify commerce and gallery interactions. No client currency conversion.
 * Product photos and notes use the browser's multipart form POST directly to Shopify.
 */
(() => {
  'use strict';
  document.documentElement.classList.replace('no-js', 'js');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scrollStories = new Set();
  let framePending = false;

  function reveal(story, value) {
    const amount = Math.min(100, Math.max(0, Number(value) || 0));
    story.querySelector('.comparison-media').style.setProperty('--reveal', amount + '%');
    story.querySelector('[data-compare-range]').value = String(amount);
    story.querySelector('[data-comparison-label]').textContent = amount < 50 ? story.dataset.photoLabel : story.dataset.paintingLabel;
    const button = story.querySelector('[data-compare-toggle]');
    button.textContent = (amount < 50 ? story.dataset.seePainting : story.dataset.seePhoto) + ' ↗';
    button.setAttribute('aria-pressed', amount < 50 ? 'true' : 'false');
    button.setAttribute('aria-label', amount < 50 ? story.dataset.showPainting : story.dataset.showPhoto);
  }

  function updateStories() {
    framePending = false;
    const viewport = window.innerHeight;
    for (const story of scrollStories) {
      if (!story.isConnected) { scrollStories.delete(story); continue; }
      if (story.dataset.manual === 'true') continue;
      if (reducedMotion.matches) { reveal(story, 100); continue; }
      const media = story.querySelector('.comparison-media').getBoundingClientRect();
      if (media.bottom > 0 && media.top < viewport) reveal(story, (viewport * .87 - media.top) / (viewport * .6) * 100);
    }
  }
  function queueStories() {
    if (framePending) return;
    framePending = true;
    window.requestAnimationFrame(updateStories);
  }

  function initHero(hero) {
    const play = () => {
      if (reducedMotion.matches || !hero.isConnected) return;
      hero.classList.remove('is-playing');
      void hero.offsetWidth;
      hero.classList.add('is-playing');
    };
    hero.querySelector('[data-hero-replay]')?.addEventListener('click', play);
    const image = hero.querySelector('.hero-hand');
    if (image?.complete) play();
    else image?.addEventListener('load', play, { once: true });
  }

  function initProduct(product) {
    const price = product.querySelector('[data-price]');
    const quantity = product.querySelector('[data-quantity]');
    const status = product.querySelector('[data-variant-status]');
    const frameImage = product.querySelector('[data-frame-image]');
    const variantData = product.querySelector('[data-variants]');
    const form = product.querySelector('form.painting-form');
    const variantInput = product.querySelector('[data-variant-id]');
    const addButton = product.querySelector('[data-add-to-bag]');
    const photoInput = product.querySelector('[data-photo]');
    const photoError = product.querySelector('[data-photo-error]');
    const removePhoto = product.querySelector('[data-remove-photo]');
    let submitting = false;
    let variants = [];
    try {
      variants = variantData ? JSON.parse(variantData.textContent) : [];
      if (!Array.isArray(variants) || variants.some(variant => !Array.isArray(variant.options) || !Number.isFinite(variant.price))) throw new Error('Invalid product options');
    } catch {
      status.textContent = product.dataset.optionsError;
      if (addButton) addButton.disabled = true;
      form?.addEventListener('submit', event => event.preventDefault());
      return;
    }
    const currency = product.dataset.currency || 'USD';
    // Shopify supplies the market currency and price. This formats quantity totals;
    // it never applies a client-side exchange rate or guesses the buyer's location.
    const currencyFormat = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'code' });
    const update = () => {
      const copies = Math.max(1, Math.min(99, Math.floor(Number(quantity.value) || 1)));
      const copiesText = copies + ' ' + (copies === 1 ? product.dataset.copy : product.dataset.copies);
      let finish = 'walnut';
      if (variantData) {
        const checked = [...product.querySelectorAll('[data-product-option]:checked')];
        const selections = checked.sort((a,b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex)).map(input => input.value);
        const selected = variants.find(variant => variant.options.length === selections.length && variant.options.every((option,index) => option === selections[index]));
        finish = ['natural','walnut','black'].find(candidate => selections.some(option =>
          option.toLowerCase() === candidate || option.toLowerCase() === product.dataset[candidate + 'Label']?.toLowerCase()
        )) || 'walnut';
        if (selected) {
          price.textContent = copies === 1 ? selected.priceText : currencyFormat.format(selected.price * copies / 100);
          status.textContent = selections.join(' · ') + ' · ' + copiesText + (selected.available ? '' : ' · ' + product.dataset.currentlyUnavailable);
        } else { price.textContent = '—'; status.textContent = product.dataset.unavailable; }
        if (variantInput) variantInput.value = selected ? String(selected.id) : '';
        if (addButton) {
          addButton.disabled = submitting || !selected?.available;
          addButton.textContent = submitting ? product.dataset.addingLabel : selected?.available ? product.dataset.addLabel : product.dataset.currentlyUnavailable;
        }
      } else {
        const size = Number(product.querySelector('[data-demo-size]:checked')?.value || 0);
        finish = product.querySelector('[data-demo-frame]:checked')?.value || 'walnut';
        const referencePrices = [99,129,169,229,299,399];
        price.textContent = '$' + (referencePrices[size] * copies).toFixed(2) + ' USD';
        status.textContent = product.dataset.referenceCatalogue + ' · ' + copiesText;
      }
      if (frameImage.dataset[finish]) {
        frameImage.src = frameImage.dataset[finish];
        frameImage.alt = frameImage.dataset[finish + 'Alt'];
      }
    };
    const validatePhoto = () => {
      if (!photoInput) return true;
      const file = photoInput.files?.[0];
      // A helpful browser check, not a server-side content or security guarantee.
      const allowedType = !file?.type || ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
      const valid = !file || (file.size > 0 && file.size <= 15 * 1024 * 1024 && /\.(jpe?g|png|webp)$/i.test(file.name) && allowedType);
      photoInput.setCustomValidity(valid ? '' : product.dataset.photoError);
      photoError.textContent = valid ? '' : product.dataset.photoError;
      photoError.hidden = valid;
      removePhoto.hidden = !file;
      return valid;
    };
    photoInput?.addEventListener('change', validatePhoto);
    removePhoto?.addEventListener('click', () => { photoInput.value = ''; validatePhoto(); photoInput.focus(); });
    form?.addEventListener('submit', event => {
      if (submitting) { event.preventDefault(); return; }
      update();
      const validPhoto = validatePhoto();
      const selected = variants.find(variant => String(variant.id) === variantInput.value);
      if (!selected?.available || !validPhoto || !form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        return;
      }
      // Keep the file input and variant/quantity enabled: native multipart POST
      // preserves the file, artist notes and Shopify's server-side error handling.
      submitting = true;
      update();
    });
    // A browser back/forward-cache restore must not leave the add button locked.
    window.addEventListener('pageshow', () => { submitting = false; update(); });
    product.addEventListener('change', event => { if (event.target === quantity) quantity.value = String(Math.max(1,Math.min(99,Math.floor(Number(quantity.value) || 1)))); update(); });
    quantity.addEventListener('input', update);
    update();
  }

  function initialize(root = document) {
    for (const hero of root.querySelectorAll('[data-hero]:not([data-initialized])')) { hero.dataset.initialized = 'true'; initHero(hero); }
    for (const story of root.querySelectorAll('[data-comparison]:not([data-initialized])')) {
      story.dataset.initialized = 'true';
      scrollStories.add(story);
      story.querySelector('[data-compare-range]').addEventListener('input', event => { story.dataset.manual = 'true'; reveal(story, event.target.value); });
      story.querySelector('[data-compare-toggle]').addEventListener('click', () => { story.dataset.manual = 'true'; reveal(story, Number(story.querySelector('[data-compare-range]').value) < 50 ? 100 : 0); });
    }
    for (const product of root.querySelectorAll('[data-product-preview]:not([data-initialized])')) { product.dataset.initialized = 'true'; initProduct(product); }
    for (const menu of root.querySelectorAll('.mobile-menu:not([data-initialized])')) {
      menu.dataset.initialized = 'true';
      menu.querySelectorAll('a').forEach(link => link.addEventListener('click', () => { menu.open = false; }));
      menu.addEventListener('keydown', event => { if (event.key === 'Escape') { menu.open = false; menu.querySelector('summary').focus(); } });
    }
    queueStories();
  }
  window.addEventListener('scroll', queueStories, { passive: true });
  window.addEventListener('resize', queueStories, { passive: true });
  reducedMotion.addEventListener('change', queueStories);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:block:select', event => { const story = event.target.closest('[data-comparison]'); if (story) { story.dataset.manual = 'true'; reveal(story,100); } });
  initialize();
})();
